import { Injectable } from '@angular/core';
import { CompanyActions, CompanySelectors, OccCompanyService, StateWithCompany } from '@company/data-access';
import { SnackTheme } from '@main/snackbar/const';
import { SnackbarFacade } from '@main/snackbar/data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AuthActions, AuthService, OCC_USER_ID_ANONYMOUS, RoutingService, UserIdService } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CompanyService } from '../services';

@Injectable()
export class AddCompanyEffects {
  authCheckBeforeAddCompany$: Observable<
    CompanyActions.AddCompanyAuthCheckSuccess | CompanyActions.AddCompanyFail | CompanyActions.SaveUnaddedCompany
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CompanyActions.AddCompany>(CompanyActions.ADD_COMPANY),
      concatLatestFrom(() => this.userIdService.getUserId()),
      switchMap(([action, userId]) => {
        if (userId === OCC_USER_ID_ANONYMOUS) {
          this.routingService.go({ cxRoute: 'login' });

          return [new CompanyActions.SaveUnaddedCompany(action.payload), new CompanyActions.AddCompanyFail()];
        }

        return of(new CompanyActions.AddCompanyAuthCheckSuccess(action.payload));
      }),
    ),
  );

  duplicateCheckBeforeAddCompany$: Observable<
    CompanyActions.AddCompanyDuplicateCheckSuccess | CompanyActions.AddCompanyFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CompanyActions.AddCompanyAuthCheckSuccess>(CompanyActions.ADD_COMPANY_AUTH_CHECK_SUCCESS),
      switchMap((action) =>
        combineLatest({
          action: of(action),
          userCompanies: this.store.select(CompanySelectors.getUserCompaniesState).pipe(
            filter((state) => Boolean(state.success)),
            map((state) => state.value || []),
            take(1),
          ),
        }),
      ),
      map(({ action, userCompanies }) => {
        if (userCompanies.some((company) => company.inn === action.payload.inn)) {
          this.snackbarFacade.setSnack({
            theme: SnackTheme.Yellow,
            title: 'Не удалось добавить компанию',
            html: `Компания <b>${action.payload.name}</b> уже есть в списке ваших компаний`,
          });

          return new CompanyActions.AddCompanyFail();
        }

        return new CompanyActions.AddCompanyDuplicateCheckSuccess(action.payload);
      }),
    ),
  );

  countCheckBeforeAddCompany$: Observable<CompanyActions.AddCompanyCountCheckSuccess | CompanyActions.AddCompanyFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<CompanyActions.AddCompanyDuplicateCheckSuccess>(CompanyActions.ADD_COMPANY_DUPLICATE_CHECK_SUCCESS),
        switchMap((action) =>
          combineLatest({
            action: of(action),
            userCompanies: this.store.select(CompanySelectors.getUserCompaniesState).pipe(
              filter((state) => Boolean(state.success)),
              map((state) => state.value || []),
              take(1),
            ),
          }),
        ),
        map(({ action, userCompanies }) => {
          const COMPANIES_MAX_COUNT_LIMIT = 10;

          if (userCompanies.length >= COMPANIES_MAX_COUNT_LIMIT) {
            this.snackbarFacade.setSnack({
              theme: SnackTheme.Yellow,
              title: 'Не удалось добавить компанию',
              html: `Превышен лимит добавленных компаний (${COMPANIES_MAX_COUNT_LIMIT})`,
            });

            return new CompanyActions.AddCompanyFail();
          }

          return new CompanyActions.AddCompanyCountCheckSuccess(action.payload);
        }),
      ),
    );

  addCompanyAfterAllChecksSuccess$: Observable<CompanyActions.AddCompanySuccess | CompanyActions.AddCompanyFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType<CompanyActions.AddCompanyCountCheckSuccess>(CompanyActions.ADD_COMPANY_COUNT_CHECK_SUCCESS),
        switchMap((action) => {
          return this.occCompanyService.addCompany(action.payload).pipe(
            map((createdCompanyUid) => new CompanyActions.AddCompanySuccess(createdCompanyUid)),
            catchError(() => of(new CompanyActions.AddCompanyFail())),
          );
        }),
      ),
    );

  addSavedCompanyAfterLoginOrSync$: Observable<CompanyActions.AddCompany> = createEffect(() =>
    this.actions$.pipe(
      ofType<AuthActions.Login | CompanyActions.SyncSavedCompanyState>(
        AuthActions.LOGIN,
        CompanyActions.SYNC_SAVED_COMPANY_STATE,
      ),
      concatLatestFrom(() => [this.store.select(CompanySelectors.getSavedCompany), this.authService.isUserLoggedIn()]),
      filter(([, data, isLoggedIn]) => Boolean(data) && isLoggedIn),
      map(([, data]) => {
        return new CompanyActions.AddCompany(data);
      }),
    ),
  );

  navigateToHomePageWhenAddingSuccess$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CompanyActions.AddCompanySuccess>(CompanyActions.ADD_COMPANY_SUCCESS),
        concatLatestFrom(() => this.routingService.getRouterState()),
        filter(([, routerState]) => routerState.state.semanticRoute === 'addCompany'),
        tap(() => this.routingService.go({ cxRoute: 'home' })),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  resetAddedCompanyAfterSelect$: Observable<CompanyActions.ResetAddedCompany> = createEffect(() =>
    this.actions$.pipe(
      ofType<CompanyActions.SelectCompany>(CompanyActions.SELECT_COMPANY),
      concatLatestFrom(() => this.companyService.getAddedCompanyUid()),
      filter(([, lastAddedCompanyUid]) => !!lastAddedCompanyUid),
      map(() => new CompanyActions.ResetAddedCompany()),
    ),
  );

  constructor(
    private actions$: Actions,
    private occCompanyService: OccCompanyService,
    private userIdService: UserIdService,
    private routingService: RoutingService,
    private companyService: CompanyService,
    private store: Store<StateWithCompany>,
    private snackbarFacade: SnackbarFacade,
    private authService: AuthService,
  ) {}
}
